import { Box, Button, CircularProgress } from "@material-ui/core";
import { uniqBy } from "ramda";
import { createContext, Dispatch, FC, SetStateAction, useEffect, useState } from "react";

import useCurrentUser from "../../../../../hooks/useCurrentUser";
import { firestore } from "../../../../../init/firebase";
import { handleUploadFilesToStorage } from "../../../../../utils/fileUpload";
import { brandsMediaSubCollRef } from "../../../../../utils/referencies";
import { applyCreateTimestamp } from "../../../../../utils/timestamp";
import { IImageUploaderContext, ILocalFileEntry, IUploadedFile } from "../types";
import ImageUploaderInput from "./ImageUploaderInput";
import ImageUploaderPreviewItems from "./ImageUploaderPreviewItems";

export const ImageUploaderContext = createContext<IImageUploaderContext>({
  onRemoveFile: undefined,
  isValidEntriesSizes: false,
  isValidEntriesLength: false,
  isValidEntriesType: false,
  maximumImages: 20,
  maximumImageSize: 10,
  uploadLoading: false,
});

const ImageUploaderContainer: FC<ImageUploaderContainerProps> = ({ triggerLoadingOnSubmit }) => {
  const [localFilesEntry, setLocalFilesEntry] = useState<ILocalFileEntry[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [isValidEntriesSizes, setIsValidEntrySize] = useState(false);
  const [isValidEntriesLength, setIsValidEntriesLength] = useState(false);
  const [isValidEntriesType, setIsValidEntriesType] = useState(false);

  const { user } = useCurrentUser();

  const handleSelectedFiles = (selectedLocalFilesEntry: ILocalFileEntry[]) => {
    setLocalFilesEntry((prevState) =>
      uniqBy((entry: ILocalFileEntry) => entry.name)([...prevState, ...selectedLocalFilesEntry])
    );
  };

  const handleRemoveFileEntry = (entry: ILocalFileEntry) => {
    setLocalFilesEntry((prevState) => prevState.filter((prevEntry) => prevEntry.localUrl !== entry.localUrl));
  };

  const uploadFilesToFirestore = async (uploadedFiles: IUploadedFile[]) => {
    const batch = firestore.batch();
    uploadedFiles.forEach((entry) => {
      batch.set(brandsMediaSubCollRef(user.id as string).doc(), applyCreateTimestamp(entry));
    });
    try {
      await batch.commit();
    } catch (ex: any) {
      console.log(ex.message);
    }
  };

  const handleUpload = async () => {
    setUploadLoading(true);
    const uploadedFiles = await handleUploadFilesToStorage(localFilesEntry);
    await uploadFilesToFirestore(uploadedFiles);
    setUploadLoading(false);
    setLocalFilesEntry([]);
  };

  const isButtonDisabled = () =>
    uploadLoading ||
    localFilesEntry.length === 0 ||
    !isValidEntriesSizes ||
    !isValidEntriesLength ||
    !isValidEntriesType;

  useEffect(() => {
    setIsValidEntriesLength(localFilesEntry.length <= 20);
    setIsValidEntrySize(!localFilesEntry.some((entry) => entry.file.size > 10 * 1e6));
    setIsValidEntriesType(!localFilesEntry.some((entry) => !entry.file.type.startsWith("image/")));
  }, [localFilesEntry]);

  useEffect(() => {
    triggerLoadingOnSubmit(uploadLoading);
  }, [uploadLoading]);

  return (
    <ImageUploaderContext.Provider
      value={{
        onRemoveFile: handleRemoveFileEntry,
        isValidEntriesSizes,
        isValidEntriesLength,
        isValidEntriesType,
        maximumImages: 20,
        maximumImageSize: 10,
        uploadLoading,
      }}
    >
      <ImageUploaderInput onSelectFiles={handleSelectedFiles} />
      <ImageUploaderPreviewItems localFilesEntry={localFilesEntry} />

      <Box display="flex">
        <Button
          variant="contained"
          color="primary"
          disabled={isButtonDisabled()}
          onClick={handleUpload}
          startIcon={uploadLoading ? <CircularProgress size={14} /> : null}
        >
          Upload
        </Button>
      </Box>
    </ImageUploaderContext.Provider>
  );
};

interface ImageUploaderContainerProps {
  triggerLoadingOnSubmit: Dispatch<SetStateAction<boolean>>;
}

export default ImageUploaderContainer;
